import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMqttCommands, setNavigateCallback, stopSound } from '../hooks/useMqttCommands';
import { useTranslation } from '../hooks/useTranslation';
import type { ActivePopup } from '../hooks/useMqttCommands';
import { Z_INDEX } from '../constants/zIndex';

/**
 * Full-screen overlay that renders incoming MQTT command popups.
 * Stacks multiple popups with the newest on top.
 * Must be rendered inside BrowserRouter.
 *
 * Auto-dismiss is handled at the module level in useMqttCommands,
 * so popups will dismiss even if this component re-renders.
 *
 * ── LAAG: `Z_INDEX.COMMAND_OVERLAY` (9940), WAS 50 — T-2.02 ─────────────────
 *
 * Op 50 stond deze overlay ONDER de screensaver en onder de slaapstand. Een
 * `show_message` op een tablet die drie minuten stil had gestaan verscheen dus achter de
 * screensaver: technisch getoond, in de praktijk onzichtbaar. Het cijfer stond los in dit
 * bestand terwijl een oproep op 9990 zat; die twee stonden nergens naast elkaar.
 *
 * ⚠ Verder is dit component ONGEWIJZIGD, en dat is opzet. Het is het vangnet voor
 * `show_message` op 800+ tablets met gemengde bundles — een oudere app die het nieuwe
 * `resident_notification` niet kent, heeft alleen dit. Nieuwe meldingen krijgen hun eigen
 * component (`NotificationDialog`), zodat dit blok niet hoeft mee te veranderen.
 */
export function CommandOverlay() {
  const { popups, dismissPopup, dismissAll, handlePopupAction } = useMqttCommands();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Register the navigate callback so useMqttCommands can trigger navigation
  useEffect(() => {
    setNavigateCallback((path: string) => navigate(path));
    return () => setNavigateCallback(() => {});
  }, [navigate]);

  if (popups.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: Z_INDEX.COMMAND_OVERLAY,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: 16,
      pointerEvents: 'auto',
    }}>
      {popups.length > 1 && (
        <button
          onClick={dismissAll}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            padding: '8px 16px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: '#fff',
            fontSize: 'var(--font-body)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {t.common.dismiss} ({popups.length})
        </button>
      )}
      <PopupCard
        popup={popups[popups.length - 1]}
        onDismiss={dismissPopup}
        onAction={handlePopupAction}
        t={t}
      />
    </div>
  );
}

function PopupCard({ popup, onDismiss, onAction, t }: {
  popup: ActivePopup;
  onDismiss: (id: string) => void;
  onAction: (popup: ActivePopup) => void;
  t: any;
}) {
  const isUrgent = popup.priority === 'urgent';

  return (
    <div style={{
      maxWidth: 500,
      width: '100%',
      backgroundColor: isUrgent ? '#dc2626' : '#fff',
      color: isUrgent ? '#fff' : '#1a202c',
      borderRadius: 20,
      padding: '28px 24px',
      boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      animation: 'slideIn 0.3s ease-out',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <span style={{ fontSize: 28 }}>
          {isUrgent ? '\u{26A0}\u{FE0F}' : '\u{1F4E8}'}
        </span>
        <div style={{ flex: 1, fontWeight: 700, fontSize: 22 }}>
          {popup.title}
        </div>
        <button
          onClick={() => onDismiss(popup.id)}
          style={{
            width: 36,
            height: 36,
            minWidth: 36,
            minHeight: 36,
            borderRadius: '50%',
            backgroundColor: isUrgent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
            color: isUrgent ? '#fff' : '#666',
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          &#10005;
        </button>
      </div>

      <div style={{
        fontSize: 18,
        lineHeight: 1.5,
        opacity: 0.9,
      }}>
        {popup.message}
      </div>

      <div style={{
        display: 'flex',
        gap: 12,
        marginTop: 8,
      }}>
        {popup.url && (
          <button
            onClick={() => onAction(popup)}
            style={{
              flex: 1,
              padding: '14px 20px',
              borderRadius: 12,
              backgroundColor: isUrgent ? '#fff' : 'var(--caro-primary)',
              color: isUrgent ? '#dc2626' : '#fff',
              fontSize: 18,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.common.ok}
          </button>
        )}
        <button
          onClick={() => {
            stopSound();
            onDismiss(popup.id);
          }}
          style={{
            flex: popup.url ? undefined : 1,
            minWidth: popup.url ? 100 : undefined,
            padding: '14px 20px',
            borderRadius: 12,
            backgroundColor: isUrgent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
            color: isUrgent ? '#fff' : '#666',
            fontSize: 18,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {t.common.close}
        </button>
      </div>
    </div>
  );
}
